import { useEffect, useMemo, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  AutofillFeedback,
  AutoFillStatus,
  ConnectionItem,
  IntakeForm,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  useToast,
} from "@smart/bridge-intake-components-dom";
import { track } from "@smart/bridge-metrics-dom";
import {
  appendDownloadFormLink,
  defaultTemplate,
} from "@smart/bridge-templates-basic";
import { familyProQuestionnaireFormUri } from "@smart/bridge-types-basic";
import { StaffDetails } from "@smart/bridge-types-basic/src/appointment";
import { useRefresh, useRestoreScrollPosition } from "@smart/itops-hooks-dom";
import { Modal, uploadFile as upload } from "@smart/itops-sb-design-system-dom";
import { entriesOf, fromEntries, jsonParse } from "@smart/itops-utils-basic";
import {
  MatterSubmission,
  useLazyLoadFields,
  useLoadSubmission,
  useUpdateAIUserFeedback,
  useUpdateResponse,
  useUpdateSubmission,
  useUploadFiles,
} from "@smart/manage-gql-client-dom";
import { useLoadFormForDisplay } from "@smart/manage-hooks-dom";

import {
  getReplaceContactNameFields,
  useOpenFamilyProForm,
} from "../../shared";
import { SubmissionTabOptions } from "../../types";

export type SubmissionActionEditProps = {
  updateResponse: ReturnType<typeof useUpdateResponse>[0];
  updateSubmission: ReturnType<typeof useUpdateSubmission>[0];
  uploadFiles: ReturnType<typeof useUploadFiles>[0];
  updateAIUserFeedback: ReturnType<typeof useUpdateAIUserFeedback>[0];
  displayProps: ReturnType<typeof useLoadFormForDisplay>;
  submission: ReturnType<typeof useLoadSubmission>;
  matterSubmission?: MatterSubmission;
  navigateTo: (destination: SubmissionTabOptions | "home") => void;
  staffDetails: StaffDetails[];
  loading?: boolean;
  previewLastSection: [
    SectionItem | "summary" | undefined,
    React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
  ];
};

export const EditResponses = ({
  updateResponse,
  updateSubmission,
  updateAIUserFeedback,
  uploadFiles,
  displayProps,
  submission,
  matterSubmission,
  previewLastSection,
  navigateTo,
  staffDetails,
  loading,
}: SubmissionActionEditProps) => {
  const { "LINK-2488": editResponsesContactSearch } = useFeatureFlags();
  const { setToasts } = useToast();
  const [connection, setConnection] = useState<ConnectionItem>({
    status: "initial",
  });
  const [submitted, setSubmitted] = useState(false);
  const [autoFillStatus, setAutoFillStatus] = useState<AutoFillStatus>();
  // Store the fields so we can update them with dynamic options
  const [fields, setFields] = useState<
    SubmissionActionEditProps["displayProps"]["fields"]
  >([]);
  const [seed, refreshSeed] = useRefresh();

  const getSectionWrapper = () => document.querySelector(".section-wrapper");
  const { setScrollPosition } = useRestoreScrollPosition({
    deps: [seed],
    getElement: getSectionWrapper,
  });
  const loadFields = useLazyLoadFields();
  const hasDynamicOptions = useMemo(
    () => fields.some((f) => f.field?.dataSourceMapping),
    [fields],
  );

  useOpenFamilyProForm({
    selectedFormUri: matterSubmission?.formUri,
    externalSubmissionEmbedUrl: matterSubmission?.externalSubmissionEmbedUrl,
  });

  const isInternal =
    matterSubmission?.rawSubmission.values.communicationMethod ===
    "internalUse";

  const submit: ResponseSubmitter = async ({
    status,
    responses,
    lastUpdatedFieldUri,
    lastUpdatedSectionUri,
  }) => {
    const submissionUri = matterSubmission!.uri;
    const { formUri } = matterSubmission!;
    if (status === "completed")
      track("Submission Completed", {
        formUri,
        submissionUri,
      });

    try {
      setConnection({ status: "loading" });
      const updateResponseResults = await Promise.all(
        entriesOf(responses)
          .filter(([, value]) => value !== undefined)
          .map(([fieldUri, value]) =>
            updateResponse({
              variables: {
                fieldUri,
                value: JSON.stringify(value),
                submissionUri,
                formUri,
              },
            }),
          ),
      );
      const operationIds = updateResponseResults.map(
        (r) => r.data?.updateResponse.operationId,
      );

      let updateSubmissionResult;
      if (status || lastUpdatedFieldUri || lastUpdatedSectionUri) {
        updateSubmissionResult = await updateSubmission({
          variables: {
            uri: submissionUri,
            formUri,
            fields: {
              lastUpdatedFieldUri,
              lastUpdatedSectionUri,
              status,
            },
          },
        });
        operationIds.push(
          updateSubmissionResult.data?.updateSubmission.values.operationId,
        );
      }
      setConnection({ status: "success" });

      if (status === "completed") {
        if (!isInternal) {
          setToasts([
            {
              text: "Your responses have been saved",
            },
          ]);
          navigateTo("view");
        }
        setSubmitted(true);
      }

      if (hasDynamicOptions) {
        const fieldsWithDynamicOptions = await loadFields({
          formUri: matterSubmission!.formUri,
          matterId: submission.result?.values.matterId ?? undefined,
          submissionUri: submission.result?.uri,
          responses: entriesOf(responses).map(([fieldUri, value]) => ({
            fieldUri,
            value: JSON.stringify(value),
          })),
        });
        const fieldMap = fromEntries(
          fieldsWithDynamicOptions?.map((f) => [f.uri, f.values]) ?? [],
        );
        setFields((existingFields) =>
          existingFields.map((f) =>
            f.field?.dataSourceMapping && fieldMap[f.uri]
              ? { ...f, options: fieldMap[f.uri].options }
              : f,
          ),
        );
      }

      return {
        operationIds: operationIds.filter(Boolean) as string[],
        submissionStatus: {
          status:
            status ||
            updateSubmissionResult?.data?.updateSubmission.values.status,
          deleted: updateSubmissionResult?.data?.updateSubmission.values
            .deleted as boolean | undefined,
        },
      };
    } catch (err) {
      setConnection({ status: "error", error: String(err) });
      return undefined;
    }
  };

  if (matterSubmission?.formUri === familyProQuestionnaireFormUri) return null;

  const statusToDisplay = () => {
    switch (matterSubmission?.status) {
      case "completed":
        return "completed";
      case "cancelled":
        return "closed";
      default:
        return undefined;
    }
  };
  const { aiFillFromMatterInfo, aiFillFiles } = submission.result?.values || {};

  useEffect(() => {
    if (!aiFillFromMatterInfo || aiFillFiles?.length) return;
    if (
      matterSubmission?.status === "completed" ||
      matterSubmission?.status === "cancelled"
    )
      return;

    const { status: aiUserFeedbackStatus } =
      submission.result?.aiUserFeedback?.values || {};

    switch (aiUserFeedbackStatus) {
      case "reviewed":
        setAutoFillStatus(undefined);
        break;
      case "generated":
      case "completed":
        setAutoFillStatus("completed");
        break;
      case "generating":
        setAutoFillStatus("filling");
        break;
      case "failed":
        setAutoFillStatus("failed");
        break;
      default:
        setAutoFillStatus("filling");
    }
  }, [
    submission.result?.aiUserFeedback?.values.status,
    matterSubmission?.status,
  ]);

  useEffect(() => {
    if (!fields.length && displayProps.fields.length) {
      setFields(displayProps.fields);
    }
  }, [displayProps.fields]);

  return (
    <>
      {!displayProps.loading && displayProps.exists && submission.result && (
        <IntakeForm
          {...displayProps}
          fields={fields}
          key={
            autoFillStatus === "filling"
              ? JSON.stringify(submission.result?.responses)
              : seed
          }
          autoFillStatus={autoFillStatus}
          form={{
            uri: matterSubmission?.formUri || "",
            ...displayProps.form,
            response: appendDownloadFormLink(
              displayProps.form.response || defaultTemplate.response,
            ),
          }}
          lookup={{
            addressLookup: displayProps.lookup.addressLookup,
            fileLookup: {
              upload,
              load: async ({ fieldUri, fileNames }) => {
                const result = await uploadFiles({
                  variables: {
                    fieldUri,
                    fileNames,
                    formUri: matterSubmission!.formUri,
                    submissionUri: matterSubmission!.uri,
                  },
                });
                return result.data?.uploadFiles;
              },
            },
          }}
          connection={connection}
          submission={{
            ...submission.result.values,
            autoFillStatus: submission.result.aiUserFeedback?.values.status,
            responses: fromEntries(
              submission.result.responses.map((r) => [
                r.fieldUri,
                {
                  value: jsonParse(r.value, "parse response"),
                  syncStatus: r.syncStatus,
                },
              ]),
            ) as SubmissionItem["responses"],
          }}
          submitResponses={submit}
          isInternal={isInternal}
          loadResponses={async () => undefined}
          staffDetails={staffDetails}
          newUI
          loading={loading}
          isAutoFillFileUploadDisabled
          fieldComponentOverride={
            editResponsesContactSearch
              ? getReplaceContactNameFields(() => {
                  setScrollPosition(getSectionWrapper()?.scrollTop || 0);
                  refreshSeed();
                })
              : undefined
          }
          previewOptions={{ previewLastSection }}
          postSubmissionStatus={{
            status: submission.result?.values.status,
          }}
          showPaymentPlaceholder
          disableSignatureEditing
        />
      )}
      <Modal
        header={{
          icon: "solidCheck",
          iconBgColor: "bg-green-100",
          text: `This form has been ${statusToDisplay()}`,
        }}
        onClose={() => navigateTo("view")}
        open={!!statusToDisplay() && !submitted}
        footer={{
          buttons: [
            {
              text: "OK",
              variant: "primaryDefault",
              onClick: () => navigateTo("view"),
            },
          ],
        }}
        loading={displayProps.loading}
      >
        <p className="text-ui">
          The form you are trying to edit has been marked as {statusToDisplay()}{" "}
          and responses can no longer be edited.
        </p>
      </Modal>
      <Modal
        header={{
          icon: "solidCheck",
          iconBgColor: "bg-green-100",
          text: "Form submitted",
        }}
        onClose={() => navigateTo("view")}
        open={
          submitted &&
          isInternal &&
          !aiFillFromMatterInfo &&
          !aiFillFiles?.length
        }
        footer={{
          buttons: [
            {
              text: "OK",
              variant: "primaryDefault",
              onClick: () => navigateTo("view"),
            },
          ],
        }}
        dataTestId="form-submitted-modal"
      >
        <p className="text-ui">Your form has been submitted successfully.</p>
      </Modal>
      <AutofillFeedback
        title="Your form has been submitted"
        open={submitted && autoFillStatus === "completed" && isInternal}
        onSubmit={async (feedback) => {
          if (!submission.result?.aiUserFeedback?.uri) return;

          await updateAIUserFeedback({
            variables: {
              uri: submission.result?.aiUserFeedback?.uri,
              feedback,
              status: "reviewed",
            },
          });
          navigateTo("view");
        }}
      />
    </>
  );
};
